import { FEED_RUN_TRIGGERS, type FeedRun } from '@fruuit/shared'
import { type Column, DataTable, type DataTablePagination, EmptyState } from '@fruuit/ui'
import { Rss } from 'lucide-react'
import { RunStatusBadge } from './badges'
import { formatCount, formatDateTime, formatDuration } from './format'

function Counts({ run }: { run: FeedRun }) {
  if (run.status === 'failed' || run.returned_count === null) {
    return (
      <span className="text-sage" title="Counts are unknown for a failed run">
        —
      </span>
    )
  }
  return (
    <dl className="grid grid-cols-[auto_auto] gap-x-2 text-xs leading-5 tabular-nums">
      <dt className="text-sage">Returned</dt>
      <dd className="text-right text-ink">{formatCount(run.returned_count)}</dd>
      <dt className="text-sage">New</dt>
      <dd className="text-right text-ink">{formatCount(run.imported_count)}</dd>
      <dt className="text-sage">Reused</dt>
      <dd className="text-right text-ink">{formatCount(run.reused_count)}</dd>
      <dt className="text-sage">Rejected</dt>
      <dd
        className={`text-right ${run.rejected_count ? 'font-semibold text-attention' : 'text-ink'}`}
      >
        {formatCount(run.rejected_count)}
      </dd>
    </dl>
  )
}

function Notes({ run }: { run: FeedRun }) {
  const notes: string[] = []
  if (run.error_code) {
    notes.push(
      `${run.error_label ?? run.error_code}${run.http_status ? ` (HTTP ${run.http_status})` : ''}`,
    )
  }
  if (run.cap_reached) notes.push('Result cap reached.')
  notes.push(...run.limitations)
  if (run.next_retry_at) notes.push(`Retry scheduled for ${formatDateTime(run.next_retry_at)}.`)
  if (notes.length === 0) return <span className="text-sage">No gaps recorded</span>
  return (
    <ul className="flex max-w-[22rem] flex-col gap-1 text-xs leading-relaxed text-ink-soft">
      {notes.map((note) => (
        <li key={note} className="flex gap-1.5">
          <span aria-hidden="true" className="mt-[7px] size-1 shrink-0 rounded-full bg-sage" />
          {note}
        </li>
      ))}
    </ul>
  )
}

const COLUMNS: Column<FeedRun>[] = [
  {
    id: 'run',
    header: 'Scheduled',
    cell: (run) => (
      <div className="min-w-[10rem]">
        <p className="font-medium whitespace-nowrap text-ink">
          {formatDateTime(run.scheduled_for ?? run.started_at)}
        </p>
        <p className="text-xs text-sage">
          {FEED_RUN_TRIGGERS.labels[run.trigger]}
          {run.attempt > 1 ? ` · attempt ${run.attempt}` : ''}
          {run.requested_by_name ? ` · ${run.requested_by_name}` : ''}
        </p>
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    className: 'whitespace-nowrap',
    cell: (run) => <RunStatusBadge status={run.status} />,
  },
  {
    id: 'started',
    header: 'Started',
    cell: (run) => (
      <div className="whitespace-nowrap">
        <p className="text-ink">{formatDateTime(run.started_at)}</p>
        <p className="text-xs text-sage">Took {formatDuration(run.duration_ms)}</p>
      </div>
    ),
  },
  { id: 'records', header: 'Records', cell: (run) => <Counts run={run} /> },
  {
    id: 'signals',
    header: 'Signals',
    align: 'right',
    cell: (run) => <span className="tabular-nums">{run.signal_count}</span>,
  },
  { id: 'notes', header: 'Errors and gaps', cell: (run) => <Notes run={run} /> },
]

export function RunTable({
  runs,
  loading,
  pagination,
}: {
  runs: readonly FeedRun[]
  loading: boolean
  pagination: DataTablePagination
}) {
  return (
    <DataTable
      caption="Feed run history, newest first"
      columns={COLUMNS}
      rows={runs}
      getRowId={(run) => run.id}
      loading={loading}
      pagination={pagination}
      empty={
        <EmptyState
          variant="plain"
          icon={<Rss />}
          title="No runs to show"
          description="Runs appear here after the hourly schedule or a manual re-run."
        />
      }
    />
  )
}
